/**
 * SHAH - Smart Hub for AI & Humans
 * Landing page + Floating chatbot console
 */

import React, { useState, useEffect, useRef } from 'react';
import ShahService from './services/ShahService';
import './styles/ShahApp.css';

// Client-side markdown and format parsing to match original live site
const formatMessage = (text) => {
  if (!text) return '';
  
  // Escape HTML first
  let s = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks: ```code``` (must be before inline processing)
  s = s.replace(/```(\w*)\n?([\s\S]*?)```/g, function(_, lang, code) {
    return '<pre><code class="lang-' + (lang || '') + '">' + code.trim() + '</code></pre>';
  });

  // Headings: # ## ### (at start of line)
  s = s.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  s = s.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  s = s.replace(/^# (.+)$/gm, '<h2>$1</h2>');

  // Tables: | col | col |
  s = s.replace(/((?:^\|.+\|$\n?)+)/gm, function(table) {
    const rows = table.trim().split('\n');
    let html = '<table>';
    rows.forEach(function(row, idx) {
      // Skip separator rows (|---|---|)
      if (/^\|[\s\-:]+\|$/.test(row)) return;
      const cells = row.split('|').filter(function(c) { return c.trim() !== ''; });
      const tag = idx === 0 ? 'th' : 'td';
      html += '<tr>';
      cells.forEach(function(cell) {
        html += '<' + tag + '>' + cell.trim() + '</' + tag + '>';
      });
      html += '</tr>';
    });
    html += '</table>';
    return html;
  });

  // Horizontal rule: --- or ***
  s = s.replace(/^[-*]{3,}$/gm, '<hr>');

  // Unordered lists: - item or * item
  s = s.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');
  s = s.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');

  // Ordered lists: 1. item
  s = s.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Inline code: `code`
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Bold + italic: ***text*** or ___text___
  s = s.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>');

  // Bold: **text** or __text__
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_
  s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  s = s.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');

  // Strikethrough: ~~text~~
  s = s.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  // Links: [text](url)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  // Line breaks (only for lines not already wrapped in block elements)
  s = s.replace(/\n/g, '<br>');

  // Clean up double <br> after block elements
  s = s.replace(/(<\/h[234]>)<br>/g, '$1');
  s = s.replace(/(<\/table>)<br>/g, '$1');
  s = s.replace(/(<\/pre>)<br>/g, '$1');
  s = s.replace(/(<\/ul>)<br>/g, '$1');
  s = s.replace(/(<hr>)<br>/g, '$1');

  return s;
};

const ShahApp = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Initialize Session & Load Saved History
  useEffect(() => {
    // Session ID
    let sid = localStorage.getItem('shah_session_id');
    if (!sid) {
      sid = 'web-' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('shah_session_id', sid);
    }
    sessionIdRef.current = sid;

    // Load messages from localStorage
    const savedState = localStorage.getItem('shah_chat_state');
    if (savedState) {
      try {
        setMessages(JSON.parse(savedState));
      } catch (err) {
        console.warn('Failed to parse saved chat state:', err);
      }
    } else {
      // Welcome message
      const welcome = {
        role: 'assistant',
        text: "Hello! I'm SHAH, a multi-channel AI assistant. I can help you learn about this project, answer technical questions, or assist with code-related tasks. Ask me anything!",
        ts: Date.now()
      };
      setMessages([welcome]);
      localStorage.setItem('shah_chat_state', JSON.stringify([welcome]));
    }
  }, []);

  // Scroll to bottom when messages or typing status updates
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  // Keep localStorage sync'd
  const saveChatHistory = (updated) => {
    setMessages(updated);
    try {
      localStorage.setItem('shah_chat_state', JSON.stringify(updated.slice(-100)));
    } catch (err) {
      console.warn('Failed to save chat history to localStorage:', err);
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    
    // Auto-resize input
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 96) + 'px';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    const text = inputText.trim();
    if (!text || isSending) return;

    const userMsg = { role: 'user', text, ts: Date.now() };
    const updatedWithUser = [...messages, userMsg];
    saveChatHistory(updatedWithUser);

    setInputText('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    setIsSending(true);

    try {
      const result = await ShahService.sendChatMessage(
        text,
        sessionIdRef.current,
        {
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          referrer: document.referrer || '',
          screen: window.innerWidth + 'x' + window.innerHeight,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      );

      if (result.poll_url || result.job_id) {
        pollForResponse(result.job_id);
      } else if (result.response) {
        // Direct response fallback
        const assistantMsg = { role: 'assistant', text: result.response, ts: Date.now() };
        saveChatHistory([...updatedWithUser, assistantMsg]);
        setIsSending(false);
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      const errMsg = {
        role: 'assistant',
        text: "I'm having trouble connecting right now. Please try again in a moment.",
        ts: Date.now(),
        isError: true
      };
      saveChatHistory([...updatedWithUser, errMsg]);
      setIsSending(false);
    }
  };

  const pollForResponse = async (jobId, attempt = 1, maxAttempts = 60) => {
    if (attempt > maxAttempts) {
      const timeoutMsg = {
        role: 'assistant',
        text: "The request timed out. The local SHAH Gateway might be offline or busy. Please try again later.",
        ts: Date.now(),
        isError: true
      };
      setMessages(prev => {
        const updated = [...prev, timeoutMsg];
        localStorage.setItem('shah_chat_state', JSON.stringify(updated.slice(-100)));
        return updated;
      });
      setIsSending(false);
      return;
    }

    try {
      const status = await ShahService.pollChatResponse(sessionIdRef.current, jobId);
      if (status.status === 'completed') {
        const replyMsg = { role: 'assistant', text: status.response, ts: Date.now() };
        setMessages(prev => {
          const updated = [...prev, replyMsg];
          localStorage.setItem('shah_chat_state', JSON.stringify(updated.slice(-100)));
          return updated;
        });
        setIsSending(false);
      } else if (status.status === 'failed') {
        const failedMsg = {
          role: 'assistant',
          text: status.response || 'I encountered an error during execution. Please try again.',
          ts: Date.now(),
          isError: true
        };
        setMessages(prev => {
          const updated = [...prev, failedMsg];
          localStorage.setItem('shah_chat_state', JSON.stringify(updated.slice(-100)));
          return updated;
        });
        setIsSending(false);
      } else {
        // Still pending or in_progress, poll again after 1.5 seconds
        setTimeout(() => pollForResponse(jobId, attempt + 1), 1500);
      }
    } catch (err) {
      console.warn('Polling error:', err);
      setTimeout(() => pollForResponse(jobId, attempt + 1), 1500);
    }
  };

  return (
    <div className="shah-landing-wrapper">
      {/* Header */}
      <header className="site-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <span className="logo-icon">S</span>
              <span className="logo-text">SHAH</span>
            </div>
            <nav className="nav">
              <a href="#features">Features</a>
              <a href="#examples">Examples</a>
              <a href="#architecture">Architecture</a>
              <a href="#languages">Domains</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1>Smart Hub for AI &amp; Humans</h1>
          <p className="hero-tagline">
            An autonomous AI orchestration platform that generates, tests, and validates
            production-quality work across 75+ domains and 18 industries &mdash; from
            software engineering to mechanical, chemical, biomedical, and aerospace engineering.
          </p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">75+</span>
              <span className="stat-label">Domains</span>
            </div>
            <div className="stat">
              <span className="stat-number">18</span>
              <span className="stat-label">Industries</span>
            </div>
            <div className="stat">
              <span className="stat-number">3,000+</span>
              <span className="stat-label">Test Cases</span>
            </div>
            <div className="stat">
              <span className="stat-number">31</span>
              <span className="stat-label">AI Tools</span>
            </div>
            <div className="stat">
              <span className="stat-number">9</span>
              <span className="stat-label">Channels</span>
            </div>
            <div className="stat">
              <span className="stat-number">23</span>
              <span className="stat-label">Languages</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2>Capabilities</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3>75+ Engineering Domains</h3>
              <p>
                Not just software &mdash; SHAH validates work across 18 industries:
                mechanical (FEA, CFD, robotics, CNC), electrical (VHDL, SPICE, DSP),
                chemical (reaction kinetics, distillation), biomedical, aerospace,
                civil engineering, and more.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
              </div>
              <h3>23 Programming Languages</h3>
              <p>
                From Python and Rust to COBOL and Ada. Each language has a dedicated
                Docker sandbox, test framework, and L2 validation pipeline. 921 software
                engineering tests across all 23 languages.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
              </div>
              <h3>9 Communication Channels</h3>
              <p>
                One brain, every channel: WhatsApp, Telegram, Slack, Discord, Signal,
                Email (Gmail + Outlook), LinkedIn, this website, and generic webhooks.
                31 built-in tools, shared memory, and unified CRM across all
                channels simultaneously.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3>Self-Improving Test Loop</h3>
              <p>
                The autonomous runner launches 3,000+ tests, triages failures with AI,
                applies fixes, and re-runs. It runs unattended for days, converging
                toward 100% pass rate. Every fix is committed and tested automatically.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <h3>Business Intelligence Suite</h3>
              <p>
                LLM-powered process mining discovers business stages from email history.
                CRM tracks every interaction across all channels. Email autopilot drafts
                replies (never sends without approval). Digital twin learns your writing
                style. Proactive morning briefings and evening digests.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 20V10"/>
                  <path d="M18 20V4"/>
                  <span className="logo-icon" style={{display: 'none'}}>OS</span>
                  <polyline points="6 20 6 16 18 16 18 20" style={{display: 'none'}}/>
                </svg>
              </div>
              <h3>Personal AI Operating System</h3>
              <p>
                Persistent cross-session memory. 5 built-in personalities + unlimited custom.
                Autonomous agent mode (up to 8 hours unattended). Semantic search across
                all your documents. MCP extensibility. AWS website deployment. Free image
                generation, voice synthesis, and transcription.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section id="architecture" className="architecture">
        <div className="container">
          <h2>How It Works</h2>
          <div className="arch-flow">
            <div className="arch-step">
              <div className="arch-number">1</div>
              <h3>Receive</h3>
              <p>Message arrives via any channel (Telegram, Discord, website, email, etc.)</p>
            </div>
            <div className="arch-arrow">&rarr;</div>
            <div className="arch-step">
              <div className="arch-number">2</div>
              <h3>Route</h3>
              <p>Gateway classifies the request and selects the optimal AI provider and model</p>
            </div>
            <div className="arch-arrow">&rarr;</div>
            <div className="arch-step">
              <div className="arch-number">3</div>
              <h3>Orchestrate</h3>
              <p>Supervisor plans the task, worker executes with 31 tools (file I/O, bash, search)</p>
            </div>
            <div className="arch-arrow">&rarr;</div>
            <div className="arch-step">
              <div className="arch-number">4</div>
              <h3>Validate</h3>
              <p>Docker sandbox runs tests, security scans, and integrity checks automatically</p>
            </div>
            <div className="arch-arrow">&rarr;</div>
            <div className="arch-step">
              <div className="arch-number">5</div>
              <h3>Deliver</h3>
              <p>Results returned to the original channel with full execution log and artifacts</p>
            </div>
          </div>
        </div>
      </section>

      {/* Domains Section */}
      <section id="languages" className="languages">
        <div className="container">
          <h2>75+ Domains Across 18 Industries</h2>
          <p className="languages-subtitle">
            Each domain has validated test cases with Docker-sandboxed execution.
            From 23 programming languages to FEA solvers, circuit simulators, and drug design.
          </p>
          <div className="languages-grid">
            <span className="lang-tag" title="22 languages, 881 tests">Software Engineering</span>
            <span className="lang-tag" title="SQL optimization, ETL, migrations">Data Engineering</span>
            <span className="lang-tag" title="Terraform, Docker/K8s">DevOps / Infrastructure</span>
            <span className="lang-tag" title="Solidity smart contracts">Blockchain / Web3</span>
            <span className="lang-tag" title="Lean 4 theorem proving">Formal Methods</span>
            <span className="lang-tag" title="VHDL, SPICE, PCB, power grid, antenna, DSP">Electrical Engineering</span>
            <span className="lang-tag" title="FEA, CFD, CNC, robotics, composites, vehicle dynamics">Mechanical Engineering</span>
            <span className="lang-tag" title="Steel (AISC), concrete (ACI), pipe networks">Civil / Structural</span>
            <span className="lang-tag" title="Reaction kinetics, distillation, mass balance">Chemical Engineering</span>
            <span className="lang-tag" title="EnergyPlus building simulation">Building / Energy</span>
            <span className="lang-tag" title="Wastewater treatment">Environmental</span>
            <span className="lang-tag" title="Orbital mechanics">Aerospace</span>
            <span className="lang-tag" title="LP/MIP optimization, vehicle routing, queueing">Operations Research</span>
            <span className="lang-tag" title="Fault tree, Markov reliability">Reliability Engineering</span>
            <span className="lang-tag" title="Drug design, bioinformatics, RNA, molecular dynamics, clinical trials, genomics">Biomedical / Healthcare</span>
            <span className="lang-tag" title="Geospatial analysis, mapping">GIS / Geospatial</span>
            <span className="lang-tag" title="DXF validation">CAD / Drafting</span>
            <span className="lang-tag" title="Process mining from email history">Business Intelligence</span>
          </div>
          <h3 style={{ textAlign: 'center', marginTop: '2rem', color: '#9ca3af' }}>Programming Languages</h3>
          <div className="languages-grid" style={{ marginTop: '1rem' }}>
            <span className="lang-tag">Python</span>
            <span className="lang-tag">JavaScript</span>
            <span className="lang-tag">TypeScript</span>
            <span className="lang-tag">C</span>
            <span className="lang-tag">C++</span>
            <span className="lang-tag">Rust</span>
            <span className="lang-tag">Go</span>
            <span className="lang-tag">Java</span>
            <span className="lang-tag">C#</span>
            <span className="lang-tag">Kotlin</span>
            <span className="lang-tag">Swift</span>
            <span className="lang-tag">Dart</span>
            <span className="lang-tag">PHP</span>
            <span className="lang-tag">Ruby</span>
            <span className="lang-tag">Perl</span>
            <span className="lang-tag">R</span>
            <span className="lang-tag">MATLAB</span>
            <span className="lang-tag">Pascal</span>
            <span className="lang-tag">Visual Basic</span>
            <span className="lang-tag">COBOL</span>
            <span className="lang-tag">Fortran</span>
            <span className="lang-tag">Ada</span>
            <span className="lang-tag">SQL</span>
          </div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section id="differentiators" className="architecture">
        <div className="container">
          <h2>What SHAH Does That Others Can't</h2>
          <p className="languages-subtitle">
            Not a chatbot. A personal AI operating system with unified memory,
            multi-channel presence, and proven code execution.
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <h3>Proven Code</h3>
              <p>No other AI proves its code works. SHAH runs 3,000+ tests in Docker sandboxes
              and grades every output A++ to F. You see the test results, not just the code.</p>
            </div>
            <div className="feature-card">
              <h3>One Brain, Every Channel</h3>
              <p>Send a task on WhatsApp, get the update on Telegram, review results on Slack.
              Same memory, same personality, same context &mdash; no re-explaining.</p>
            </div>
            <div className="feature-card">
              <h3>Autonomous Research</h3>
              <p>Works independently for up to 8 hours. Breaks complex tasks into sub-tasks,
              executes with 31 tools, and sends progress updates. No babysitting required.</p>
            </div>
            <div className="feature-card">
              <h3>Learns Your Business</h3>
              <p>Discovers business processes from your email history. Learns your writing style.
              Drafts replies indistinguishable from you. Remembers everything, forever.</p>
            </div>
            <div className="feature-card">
              <h3>Beyond Software</h3>
              <p>FEA stress analysis, SPICE circuit simulation, drug molecule design, orbital mechanics,
              Lean 4 theorem proving. 75+ domains across mechanical, chemical, biomedical, and aerospace.</p>
            </div>
            <div className="feature-card">
              <h3>Security-First</h3>
              <p>3-layer defense: prompt injection detection, PII redaction, output leak scanning.
              Website visitors get restricted tool access. Your private data stays private.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section id="examples" className="features">
        <div className="container">
          <h2>Example Prompts</h2>
          <p className="languages-subtitle">
            Real tasks you can give SHAH. Each one triggers multiple tools, searches, and actions automatically.
          </p>
          <div className="features-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Business Intelligence</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Analyze my last 3 years of email with insurance clients.
                Discover every stage a deal goes through, how long each takes,
                where deals stall, and which templates close fastest.
                Then draft a follow-up for every stale thread over 14 days.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Cross-Channel Campaign</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Find all contacts who work in fintech from my LinkedIn, email,
                and WhatsApp. Draft a personalized outreach message for each one
                matching my writing style, send via their preferred channel,
                and schedule follow-ups for anyone who doesn't reply in 3 days.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Full-Stack Engineering</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Build a Rust WebSocket server with JWT auth, rate limiting,
                and connection pooling. Generate the code, write 40 tests,
                run them in Docker, and if anything fails, fix it until
                every test passes. Send me the graded result on Telegram.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Competitive Intelligence</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Research the top 5 competitors in the AI agent framework space.
                For each one, find their GitHub stars, last commit date, pricing,
                key features, and weaknesses. Compile into a PDF report,
                publish a summary on the website, and email it to the team.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Engineering Simulation</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Design a heat exchanger for a 500kW cooling system.
                Generate the LMTD calculations in Python, validate with
                ASME thermal standards, run a CFD mesh quality check,
                and produce a technical spec PDF with all parameters.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Morning Autopilot</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Every morning at 7:30, check my unread emails, draft replies
                to anything urgent, summarize overnight Slack messages,
                pull today's calendar from my inbox, search for news about
                my industry, and send me a briefing on WhatsApp before I wake up.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Due Diligence</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;I'm meeting with Acme Corp tomorrow. Pull everything we have &mdash;
                emails, LinkedIn messages, CRM interactions, any documents.
                Search the web for their latest news, funding rounds, and leadership
                changes. Generate a one-page briefing and read it to me as audio.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Blog Publisher</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Write an article about how AI is transforming the insurance
                industry. Use my writing style from past emails. Include 3 real
                data points from web research. Publish it on the website,
                generate a social media summary, and send it to my LinkedIn contacts.&rdquo;
              </p>
            </div>
            <div className="feature-card">
              <h3 style={{ color: '#4f8ff7', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Autonomous Deep Research</h3>
              <p style={{ fontStyle: 'italic', color: '#e4e4e7', lineHeight: '1.6' }}>
                &ldquo;Spend the next 4 hours researching drug-protein interaction
                databases. Write Python scripts to parse PDB files, run molecular
                docking simulations, validate binding affinity calculations,
                and compile results into a research report. Update me every 15 minutes.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Self-Improvement Section */}
      <section className="architecture" id="self-improvement">
        <div className="container">
          <h2>Three Self-Improvement Loops</h2>
          <p className="languages-subtitle">
            SHAH doesn't just work &mdash; it gets better on its own. Three independent
            feedback loops running simultaneously, each improving a different domain.
            No other AI system has all three.
          </p>
          <div className="features-grid">
            <div className="feature-card" style={{ borderLeft: '4px solid #ef4444' }}>
              <h3>Loop 1: Code Infrastructure</h3>
              <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>auto_runner &mdash; fixes the plane while flying it</p>
              <p>When a test fails, SHAH analyzes its own source code, writes a fix,
              commits it to git, and re-runs the test to prove it works.
              <strong>46+ autonomous commits</strong> &mdash; Swift test pipelines built from scratch,
              Pascal harnesses rewritten, Rust error parsers fixed.
              All without human involvement.</p>
              <p style={{ marginTop: '0.5rem' }}><strong>Verification:</strong> Binary &mdash; test passes or fix is discarded.</p>
            </div>
            <div className="feature-card" style={{ borderLeft: '4px solid #3b82f6' }}>
              <h3>Loop 2: User Preferences</h3>
              <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Agent memory &mdash; learns from every correction</p>
              <p>When you correct SHAH's behavior &mdash; "send only to WhatsApp, not email" &mdash;
              it saves the rule to persistent memory and applies it forever, across all channels.
              Continuous learning extracts facts from every conversation automatically.
              <strong>SHAH gets smarter every day</strong> without explicit "remember this" commands.</p>
              <p style={{ marginTop: '0.5rem' }}><strong>Verification:</strong> You stop correcting &mdash; the behavior is learned.</p>
            </div>
            <div className="feature-card" style={{ borderLeft: '4px solid #10b981' }}>
              <h3>Loop 3: Business Intelligence</h3>
              <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Process mining &mdash; discovers what your business actually does</p>
              <p>Analyzes years of email and LinkedIn conversations to discover your actual
              business processes &mdash; stages, timing, templates, objections. Each iteration
              refines the model: better noise filters, sharper stage detection, more accurate
              classification. <strong>Zero configuration</strong> &mdash; SHAH discovers the stages,
              you don't define them.</p>
              <p style={{ marginTop: '0.5rem' }}><strong>Verification:</strong> Model accuracy improves with each iteration.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="features" id="tools">
        <div className="container">
          <h2>What SHAH Can Do For You</h2>
          <p className="languages-subtitle">31 built-in tools, all accessible from any channel via natural language.</p>
          <div className="features-grid">
            <div className="feature-card">
              <h3>Write &amp; Test Code</h3>
              <p>SHAH can generate production-quality code in 23 programming languages,
              run it in isolated Docker sandboxes, execute automated test suites, and
              grade the result from A++ to F. You can also run quick Python scripts
              and read any file from a previous project.</p>
            </div>
            <div className="feature-card">
              <h3>Communicate For You</h3>
              <p>SHAH can send messages to your contacts via WhatsApp, Telegram, Slack,
              Signal, or Discord. It looks up contacts by name across all your address books,
              merged and deduplicated from every source, and keeps track of every
              interaction in a built-in CRM with automatic topic detection.</p>
            </div>
            <div className="feature-card">
              <h3>Handle Your Email</h3>
              <p>SHAH can search through all your archived emails from Gmail and Outlook,
              send emails with attachments and CC, draft replies to messages that need
              your attention, and run an autopilot that generates responses &mdash;
              but never sends without your explicit approval.</p>
            </div>
            <div className="feature-card">
              <h3>Research &amp; Browse</h3>
              <p>SHAH can search the internet for current information, read and extract
              content from any web page, and perform semantic search across all your indexed
              documents &mdash; emails, LinkedIn messages, chats, contacts &mdash;
              to find exactly what you need.</p>
            </div>
            <div className="feature-card">
              <h3>Create Media</h3>
              <p>SHAH can generate images from text descriptions, create PDF documents
              from markdown, read PDFs and Word files (with OCR for scanned images),
              transcribe voice messages to text, and generate natural-sounding speech
              in 12+ languages &mdash; all using free APIs, no extra cost.</p>
            </div>
            <div className="feature-card">
              <h3>Remember Everything</h3>
              <p>SHAH maintains persistent memory across all conversations and channels.
              Tell it something on WhatsApp, it remembers on Telegram. It auto-learns
              facts from your conversations and can adopt different personalities &mdash;
              5 built-in or unlimited custom, even per-contact.</p>
            </div>
            <div className="feature-card">
              <h3>Automate Your Day</h3>
              <p>SHAH can schedule recurring tasks with cron expressions, send you
              morning briefings with unread emails and news, deliver evening digests
              summarizing your day, monitor prices hourly, and check that all your
              communication channels are healthy.</p>
            </div>
            <div className="feature-card">
              <h3>Work Independently</h3>
              <p>SHAH's autonomous mode can tackle complex research projects for up to
              8 hours without supervision. It breaks tasks into sub-steps, uses all
              31 tools, and sends you progress updates every 15 minutes. State is
              saved so it can resume if interrupted.</p>
            </div>
            <div className="feature-card">
              <h3>Learn Your Business</h3>
              <p>SHAH can analyze years of email history to discover your actual business
              processes &mdash; which stages a deal goes through, how long each takes,
              where things stall. It can also learn your exact writing style and draft
              messages that sound indistinguishable from you.</p>
            </div>
            <div className="feature-card">
              <h3>Manage Your Website</h3>
              <p>SHAH can publish blog posts, update pages, and manage site configuration
              on AWS-hosted websites. It can read and write to DynamoDB databases
              directly. This very website was deployed and is managed by SHAH.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="container">
          <p>Powered by SHAH &bull; 75+ domains &bull; 18 industries &bull; 3,000+ tests &bull; Built in Belgrade</p>
        </div>
      </footer>

      {/* Floating Chat Widget */}
      <div className={`chat-widget ${isOpen ? 'open' : ''}`}>
        <button className="chat-toggle" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle chat">
          <svg className="chat-icon-open" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          <svg className="chat-icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div className="chat-panel">
          <div className="chat-header">
            <div className="chat-header-info">
              <span className="chat-header-dot"></span>
              <span className="chat-header-title">SHAH Assistant</span>
            </div>
            <button className="chat-close" onClick={() => setIsOpen(false)} aria-label="Close chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          
          <div className="chat-messages" ref={messagesContainerRef}>
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`chat-message ${msg.role} ${msg.isError ? 'error' : ''}`}
                dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
              />
            ))}
            
            {isSending && (
              <div className="typing-indicator" id="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            )}
          </div>
          
          <div className="chat-input-area">
            <textarea
              className="chat-input"
              placeholder="Ask me anything..."
              rows={1}
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isSending}
              ref={inputRef}
            />
            <button 
              className="chat-send" 
              onClick={sendMessage} 
              disabled={isSending || !inputText.trim()}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShahApp;
